import React from 'react'
import cn from 'classnames'
import FormattedCurrency from 'js/components/FormattedCurrency'
import { FormattedMessage } from 'react-intl'
import TickBox from '../Tickbox'
import ChooseSubscriptionText from './ChooseSubscriptionPriceText'
import LSButton from '../../LSButton'

interface DurationProps {
  duration: 1 | 3 | 6 | 12 | 24
  discount: boolean
  price?: React.ReactNode
}

function getDurationFrequency({ duration, discount, price }: DurationProps) {
  if (duration === 1)
    return (
      <FormattedMessage
        id="upgrade.billed_monthly"
        defaultMessage="Billed monthly"
      />
    )
  if (duration === 3)
    return discount ? (
      <FormattedMessage
        id="chooseSubscription.billed_quarterly"
        defaultMessage="Billed quarterly"
      />
    ) : (
      <FormattedMessage
        id="upgrade.billed_quarterly"
        defaultMessage="Billed {price} quarterly"
        values={{ price }}
      />
    )
  if (duration === 6)
    return discount ? (
      <FormattedMessage
        id="chooseSubscription.billed_twiceAYear"
        defaultMessage="Billed twice annually"
      />
    ) : (
      <FormattedMessage
        id="upgrade.billed_twiceAYear"
        defaultMessage="Billed {price} twice annually"
        values={{ price }}
      />
    )
  if (duration === 12)
    return discount ? (
      <FormattedMessage
        id="chooseSubscription.billed_yearly"
        defaultMessage="Billed annually"
      />
    ) : (
      <FormattedMessage
        id="upgrade.billed_yearly"
        defaultMessage="Billed {price} annually"
        values={{ price }}
      />
    )
  if (duration === 24)
    return discount ? (
      <FormattedMessage
        id="chooseSubscription.billed_everyOtherYear"
        defaultMessage="Billed every other year"
      />
    ) : (
      <FormattedMessage
        id="upgrade.billed_everyOtherYear"
        defaultMessage="Billed {price} every other year"
        values={{ price }}
      />
    )
}

function getDurationInWords({ duration, price }) {
  if (duration === 1)
    return (
      <FormattedMessage
        id="upgrade.billed_first_month"
        defaultMessage="First month {price}"
        values={{
          price,
        }}
      />
    )
  if (duration === 3)
    return (
      <FormattedMessage
        id="upgrade.billed_first_quarter"
        defaultMessage="First quarter {price}"
        values={{
          price,
        }}
      />
    )
  if (duration === 6)
    return (
      <FormattedMessage
        id="upgrade.billed_first_half_year"
        defaultMessage="First six months {price}"
        values={{
          price,
        }}
      />
    )
  if (duration === 12)
    return (
      <FormattedMessage
        id="upgrade.billed_first_year"
        defaultMessage="First year {price}"
        values={{
          price,
        }}
      />
    )
  if (duration === 24)
    return (
      <FormattedMessage
        id="upgrade.billed_first_two_years"
        defaultMessage="First two years {price}"
        values={{
          price,
        }}
      />
    )
}

type DiscountedPriceProps = {
  originalPrice: number
  currency: string
}
const DiscountedPrice = ({ originalPrice, currency }: DiscountedPriceProps) => (
  <span className="strikethrough">
    <FormattedCurrency value={originalPrice} currency={currency} />
  </span>
)

type Props = {
  price: number
  pricePerMonth: number
  originalPrice: number
  duration: 1 | 3 | 6 | 12 | 24
  selected: boolean
  onClick: () => void
  currency: string
  bestPlanInfo: any
  trialDuration: number
  onSelectProduct: (duration: 1 | 3 | 6 | 12 | 24) => void
  discount: number
  discountOnlyFirstPurchase: boolean
  isFallbackPrice: boolean
}

function ChooseSubscriptionProduct({
  price,
  pricePerMonth,
  originalPrice,
  duration,
  selected,
  onClick,
  currency,
  bestPlanInfo,
  trialDuration,
  onSelectProduct,
  discount,
  discountOnlyFirstPurchase = true,
  isFallbackPrice,
}: Props) {
  return (
    <div
      onClick={onClick}
      className={cn(
        `my-5 flex min-w-full cursor-pointer
        flex-col justify-between rounded-12 border-0 bg-white px-16
        py-24 shadow lg:items-center lg:py-12

      `,
        selected ? 'z-10 border-2 border-LSBrand shadow-none' : '',
      )}
    >
      <div className="relative flex items-center justify-between text-left lg:mb-12 lg:flex-col lg:px-16 lg:py-16 lg:text-center">
        <div className="mr-12 flex lg:mr-0">
          {bestPlanInfo}
          <div className="flex items-center">
            <TickBox hideOnDesktop selected={selected} />
            <ChooseSubscriptionText
              duration={duration}
              durationText={getDurationFrequency({
                duration,
                discount: Boolean(discount),
                price: <FormattedCurrency value={price} currency={currency} />,
              })}
            />
          </div>
        </div>

        <div className="my-auto shrink-0 items-center justify-around sm:flex lg:flex-col">
          <div
            className={
              'text-right text-20 font-bold lg:mb-0 lg:px-16 lg:text-40'
            }
            style={{ lineHeight: 1 }}
          >
            {
              <span
                className={cn({
                  'strikethrough strikethrough-tilted': Boolean(discount),
                })}
              >
                <FormattedCurrency
                  value={originalPrice / duration || pricePerMonth}
                  currency={currency}
                />
              </span>
            }
          </div>

          {Boolean(discount) && (
            <div className="ml-4 inline-block text-right text-14 font-demibold text-red lg:ml-0 lg:text-28">
              <FormattedCurrency value={pricePerMonth} currency={currency} />
            </div>
          )}
          <div className="flex items-center text-right text-14 text-TypeSub lg:text-center lg:text-17">
            <span className="hidden lg:block">
              /
              <FormattedMessage
                id="Redeem.plans.month"
                defaultMessage="month"
              />
            </span>
            <span className="lg:hidden">
              /<FormattedMessage id="SignUp.shortMon" defaultMessage="mon" />
            </span>
          </div>
        </div>
      </div>

      <div className="w-full text-left lg:text-center">
        <div className="inline text-17 lg:mb-12 lg:block">
          {Boolean(trialDuration) && (
            <span
              className="text-14 lg:text-17"
              data-uid="plan_free_trial_info"
            >
              <FormattedMessage
                defaultMessage="{numberOfDays}-day free trial included"
                id="free_trial_50_check_2"
                values={{
                  numberOfDays: trialDuration,
                }}
              />
            </span>
          )}
          <div className="text-17 text-TypeSub lg:mb-4 lg:block">
            {discount > 0 && (
              <div className="mt-6 text-14 font-demibold text-BrandDark lg:mt-0 lg:text-17">
                {discountOnlyFirstPurchase ? (
                  getDurationInWords({
                    duration,
                    price: (
                      <DiscountedPrice
                        originalPrice={originalPrice}
                        currency={currency}
                      />
                    ),
                  })
                ) : (
                  <DiscountedPrice
                    originalPrice={originalPrice}
                    currency={currency}
                  />
                )}{' '}
                <FormattedCurrency value={price} currency={currency} />
              </div>
            )}
            {isFallbackPrice && (
              <span className="hidden lg:block">
                <FormattedMessage
                  id="chooseSubscription.regularPrice"
                  defaultMessage="Regular price"
                />
              </span>
            )}
          </div>
        </div>
        <LSButton
          link={false}
          variant="primary"
          className={`
           py-16 sm:hidden lg:block
          `}
          upperCase
          onClick={() => onSelectProduct(duration)}
          data-uid={`plan_${duration}_months_button`}
          data-testid={`choose-button-${duration}`}
        >
          {duration === 1 ? (
            <FormattedMessage
              id="chooseSubscription.chooseButton_singular"
              defaultMessage="Choose {duration} month"
              values={{ duration }}
            />
          ) : (
            <FormattedMessage
              id="chooseSubscription.chooseButton_plural"
              defaultMessage="Choose {duration} months"
              values={{ duration }}
            />
          )}
        </LSButton>
      </div>
    </div>
  )
}

export default ChooseSubscriptionProduct
